import { LogIn, PenTool } from 'lucide-react';
import React from 'react';
import { UserLevelActivity } from '../../services/eventLog.service';
import Sparkline from './charts/Sparkline';
import { trendArrow, trendColor } from './utils/trend';

type Props = {
  data?: UserLevelActivity;
  loading?: boolean;
};

const UserActivitySection: React.FC<Props> = ({ data, loading }) => {
  if (!data || loading) return null;

  const calcTrend = (
    curr: number,
    prev: number
  ): { value: number; direction: 'up' | 'down' | 'flat' } | null => {
    if (prev === 0) return null;
    const pct = ((curr - prev) / prev) * 100;
    const direction = pct > 0 ? 'up' : pct < 0 ? 'down' : 'flat';

    return { value: Math.abs(pct), direction };
  };

  const loginsTrend = calcTrend(data.logins.currTotal, data.logins.prevTotal);
  const signaturesTrend = calcTrend(data.signatures.currTotal, data.signatures.prevTotal);

  const ActivityCard: React.FC<{
    title: string;
    value: number;
    trend: ReturnType<typeof calcTrend>;
    icon: React.ReactNode;
    color: string;
    sparklineColor: string;
    daily: number[];
  }> = ({ title, value, trend, icon, color, sparklineColor, daily }) => (
    <div
      className={`rounded-lg border border-gray-200 bg-white p-4 hover:shadow-md transition-all duration-200`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-3 flex-1">
          <div className={`p-2 rounded-lg ${color} flex-shrink-0`}>{icon}</div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium text-gray-700">{title}</div>
            <div className="flex items-baseline gap-2 mt-1">
              <span className="text-2xl font-bold text-gray-900">{value}</span>
              {trend && (
                <span
                  className={`text-xs font-semibold flex items-center gap-0.5 ${trendColor(trend.direction)}`}
                >
                  {trendArrow(trend.direction)} {trend.value.toFixed(0)}%
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
      {daily.length > 0 && (
        <div className="pt-2">
          <Sparkline values={daily} color={sparklineColor} />
        </div>
      )}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 pl-1">
        User Activity (System-Wide)
      </div>
      <div className="grid grid-cols-2 gap-3">
        <ActivityCard
          title="Logins"
          value={data.logins.currTotal}
          trend={loginsTrend}
          icon={<LogIn className="h-4 w-4 text-blue-600" />}
          color="bg-blue-100"
          sparklineColor="#3b82f6"
          daily={data.logins.daily}
        />

        <ActivityCard
          title="Signatures Added"
          value={data.signatures.currTotal}
          trend={signaturesTrend}
          icon={<PenTool className="h-4 w-4 text-purple-600" />}
          color="bg-purple-100"
          sparklineColor="#a855f7"
          daily={data.signatures.daily}
        />
      </div>
    </div>
  );
};

export default UserActivitySection;
